import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpResponse,
} from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, of, throwError } from 'rxjs';

import { catchError, filter, tap } from 'rxjs/operators';
import { Globals } from '../../globals';
import { CachingService } from '../caching/caching.service';
import { OfflineCommunicationService } from '../offlineCommunication/offlinecommunication.service';

@Injectable()
export class CachingInterceptorModule implements HttpInterceptor {
  private readonly cacheService: CachingService = inject(CachingService);
  private readonly globals: Globals = inject(Globals);
  private readonly offlineService: OfflineCommunicationService = inject(
    OfflineCommunicationService,
  );

  intercept<T>(
    req: HttpRequest<T>,
    next: HttpHandler,
  ): Observable<HttpEvent<T>> {
    if (
      req.method !== 'GET' &&
      req.method !== 'OPTIONS' &&
      !req.url.endsWith('/jwtapi/jwtsecurity/refresh')
    ) {
      return next.handle(req);
    }
    // check if caching is enabled
    if (!this.globals.shouldCache) {
      return next.handle(req);
    }

    if (this.offlineService.isOfflineMode) {
      if (req.method === 'OPTIONS') {
        return of(new HttpResponse<T>({ status: 200, body: '' as T }));
      }

      return this.returnCache<T>(req.url);
    }

    return this.sendRequest<T>(req, next);
  }

  /**
   * Get server response observable by sending request to `next()`.
   * Will add the response to the cache on the way out.
   */
  sendRequest<T>(req: HttpRequest<T>, next: HttpHandler): Observable<any> {
    return next.handle(req).pipe(
      filter((event) => event instanceof HttpResponse),
      tap((event) => {
        // There may be other events besides the response.
        if (event instanceof HttpResponse) {
          if (event.status >= 200 && event.status < 300) {
            this.cacheService.insertOrUpdateCache(
              req.url,
              JSON.stringify(event.body),
            );
          }
        }
      }),
      catchError((error, caught) => {
        if (error instanceof HttpErrorResponse) {
          console.error(error);
          const isNoNetworkError = this.checkNoNetworkConnection(error);

          if (isNoNetworkError) {
            this.offlineService.setOfflineMode(true);
            return this.returnCache<T>(error.url ?? '');
          }
        }

        return throwError(() => error);
      }),
    );
  }

  returnCache<T>(url: string): Observable<HttpResponse<T>> {
    const response = this.cacheService.getCache(url);
    if (response) {
      console.error('response:', response);
      return of(new HttpResponse<T>({ status: 200, body: response as T }));
    }

    return throwError(() => new Error('offline and not chached' + url));
  }

  /**
   * Helper function to check if an error is likely due to a network connection issue.
   *
   * @param error The error object to check.
   * @returns `true` if it's likely a network error, `false` otherwise.
   */
  checkNoNetworkConnection(error: any): boolean {
    return (
      error instanceof HttpErrorResponse &&
      !error.headers.keys().length &&
      !error.ok &&
      !error.status &&
      !error.error.loaded &&
      !error.error.total
    );
  }
}
